import { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X, Pencil, Trash2 } from 'lucide-react';
import { type ColumnDef, type SortingState, type ColumnSizingState } from '@tanstack/react-table';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { commissionEntryService, type CommissionEntryTotals } from '../../services/commission-entry.service';
import { manufacturerService } from '../../services/manufacturer.service';
import { distributorService } from '../../services/distributor.service';
import type {
  CommissionEntry,
  CommissionEntryType,
  Manufacturer,
  Distributor,
  PaginationMeta,
} from '../../types';
import { DataTable } from '../../components/DataTable';
import { AuditMetadata } from '../../components/AuditMetadata';
import { MultiSelect, type MultiSelectOption } from '../../components/MultiSelect';
import { MultiTypeahead, type MultiTypeaheadOption } from '../../components/MultiTypeahead';
import { Typeahead } from '../../components/Typeahead';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const TYPE_LABELS: Record<CommissionEntryType, string> = {
  commission: 'Commission',
  deduction: 'Deduction',
  bonus: 'Bonus',
};

// Backed by the integer enum values defined in CommissionEntry.entry_type
// (commission=0, deduction=1, bonus=2). Sent as q[entry_type_in][] to ransack.
const ENTRY_TYPE_OPTIONS = [
  { value: '0', label: 'Commission' },
  { value: '1', label: 'Deduction' },
  { value: '2', label: 'Bonus' },
];

// Allow digits, an optional leading minus, and up to one decimal point with at most 2 fractional digits.
const sanitizeAmount = (value: string): string => {
  if (!value) return '';
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  // Strip everything except digits and dots
  let body = trimmed.replace(/[^\d.]/g, '');
  // Keep only the first dot
  const firstDot = body.indexOf('.');
  if (firstDot !== -1) {
    body = body.slice(0, firstDot + 1) + body.slice(firstDot + 1).replace(/\./g, '');
  }
  // Cap fractional part at 2 digits
  if (firstDot !== -1) {
    const [intPart, fracPart = ''] = body.split('.');
    body = `${intPart}.${fracPart.slice(0, 2)}`;
  }
  // Strip leading zeros on integer part (but keep "0" and "0.xx")
  if (body && !body.startsWith('.')) {
    const [intPart, fracPart] = body.split('.');
    const cleanedInt = intPart.replace(/^0+(?=\d)/, '');
    body = fracPart != null ? `${cleanedInt || '0'}.${fracPart}` : (cleanedInt || '0');
  } else if (body.startsWith('.')) {
    body = `0${body}`;
  }
  if (!body) return negative ? '-' : '';
  return (negative ? '-' : '') + body;
};

// Display the stored raw value with thousands separators, preserving decimal input as the user types.
const formatAmount = (value: string | undefined | null): string => {
  if (value == null || value === '' || value === '-') return value ?? '';
  const negative = value.startsWith('-');
  const body = negative ? value.slice(1) : value;
  // Preserve a trailing dot or partial fraction while typing
  const [intPart, fracPart] = body.split('.');
  const intNum = parseInt(intPart || '0', 10);
  if (Number.isNaN(intNum)) return value;
  const formattedInt = intNum.toLocaleString('en-US');
  const formatted = fracPart != null ? `${formattedInt}.${fracPart}` : formattedInt;
  return (negative ? '-' : '') + formatted;
};

const entrySchema = z.object({
  entry_type: z.enum(['commission', 'deduction', 'bonus']),
  manufacturer_id: z.coerce.number().int().positive('Manufacturer is required'),
  distributor_id: z.coerce.number().int().positive().optional().or(z.literal('').transform(() => undefined)),
  year: z.coerce.number().int().min(1900).max(2100),
  month: z.coerce.number().int().min(1).max(12).optional().or(z.literal('').transform(() => undefined)),
  amount: z.string().min(1, 'Amount is required').regex(/^-?\d+(\.\d{1,2})?$/, 'Must be a number (max 2 decimal places)'),
  region: z.string().optional(),
  zip_code: z.string().optional(),
  notes: z.string().optional(),
}).refine(
  (data) => data.entry_type !== 'commission' || (data.distributor_id != null),
  { message: 'Distributor is required for Commission entries', path: ['distributor_id'] }
);

// Form input type — what react-hook-form holds before the resolver coerces.
// Coerced fields (manufacturer_id, year, distributor_id, month) accept `unknown`
// at the input boundary because the form binds them from string-valued <input>s
// before Zod runs `coerce.number()`.
type EntryFormInput = z.input<typeof entrySchema>;
// Resolved/output type — what `handleSubmit(onSubmit)` hands to the callback
// after the resolver runs, with FK ids and year/month as `number`.
type EntryFormData = z.output<typeof entrySchema>;

// Sentinel sent to the backend by MultiSelect to mean "match blank/null
// values for this column". The Rails BlankValueFilter concern strips it
// before passing the rest to ransack and layers a blank-OR scope on top.
const BLANK_SENTINEL = '__BLANK__';

// Index signature lets `Filters` flow into `buildListQuery({ q })` which expects
// `Record<string, QueryValue>` (string | number | array | undefined | null).
// All named fields below are subtypes of QueryValue.
interface Filters {
  // Free-text contains-search (single scalar)
  notes_cont: string;
  // Multi-select (arrays of strings; serialized as q[*_in][])
  entry_type_in: string[];
  manufacturer_id_in: string[];
  distributor_id_in: string[];
  year_in: string[];
  month_in: string[];
  region_in: string[];
  zip_code_in: string[];
  [key: string]: string | string[];
}

const EMPTY_FILTERS: Filters = {
  notes_cont: '',
  entry_type_in: [],
  manufacturer_id_in: [],
  distributor_id_in: [],
  year_in: [],
  month_in: [],
  region_in: [],
  zip_code_in: [],
};

// Mirrors the dashboard 298 KPI strip: 4 currency totals + 4 counts. Sums
// come back as decimal strings (Rails BigDecimal); coerce with parseFloat
// at format time.
const formatCurrency = (value: string | undefined): string => {
  if (value == null) return '—';
  const n = parseFloat(value);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatCount = (value: number | undefined): string =>
  value == null ? '—' : value.toLocaleString('en-US');

const TotalTile = ({ label, value }: { label: string; value: string }) => (
  <div className="card">
    <div className="text-sm font-medium text-gray-600">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
  </div>
);

export const ManualCommissionReporting = () => {
  const [entries, setEntries] = useState<CommissionEntry[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [isLoading, setIsLoading] = useState(true);
  const [totals, setTotals] = useState<CommissionEntryTotals | null>(null);

  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [yearOptions, setYearOptions] = useState<number[]>([]);
  const [regionOptions, setRegionOptions] = useState<string[]>([]);
  const [zipOptions, setZipOptions] = useState<string[]>([]);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'year', desc: true },
    { id: 'month', desc: true },
  ]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CommissionEntry | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<EntryFormInput, unknown, EntryFormData>({
    resolver: zodResolver(entrySchema),
    defaultValues: {
      entry_type: 'commission',
      year: new Date().getFullYear(),
      amount: '',
    },
  });

  const selectedType = watch('entry_type');

  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await commissionEntryService.list({ page, per_page: perPage, q: filters, sort: sorting });
      setEntries(result.data);
      setMeta(result.meta);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [page, perPage, filters, sorting]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Totals only depend on filters (not page/perPage/sorting), so they live in
  // their own effect and don't refetch when the user pages through results.
  const loadTotals = useCallback(async () => {
    try {
      const result = await commissionEntryService.totals({ q: filters });
      setTotals(result);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [filters]);

  useEffect(() => {
    loadTotals();
  }, [loadTotals]);

  // Close modal on Escape
  useEffect(() => {
    if (!showModal) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowModal(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showModal]);

  useEffect(() => {
    // Load full lookup datasets once on mount (small datasets). Includes
    // disabled partners so the page-level filter dropdowns can still match
    // historical entries linked to them. The create/edit form filters down
    // to only active partners via `activeManufacturers` / `activeDistributors`.
    Promise.all([
      manufacturerService.list({ per_page: 1000 }),
      distributorService.list({ per_page: 1000 }),
      commissionEntryService.filterOptions(),
    ])
      .then(([mfrs, dists, opts]) => {
        setManufacturers(mfrs.data);
        setDistributors(dists.data);
        setYearOptions(opts.years);
        setRegionOptions(opts.regions);
        setZipOptions(opts.zip_codes);
      })
      .catch((error) => toast.error(getErrorMessage(error)));
  }, []);

  // Form typeaheads only show active partners. If we're editing an entry
  // whose partner has since been disabled, force-include it so the field
  // doesn't go blank on edit.
  const activeManufacturers = useMemo(() => {
    const list = manufacturers.filter((m) => m.active);
    if (editingEntry && !list.some((m) => m.id === editingEntry.manufacturer.id)) {
      const linked = manufacturers.find((m) => m.id === editingEntry.manufacturer.id);
      if (linked) list.push(linked);
    }
    return list;
  }, [manufacturers, editingEntry]);

  const activeDistributors = useMemo(() => {
    const list = distributors.filter((d) => d.active);
    if (editingEntry?.distributor && !list.some((d) => d.id === editingEntry.distributor!.id)) {
      const linked = distributors.find((d) => d.id === editingEntry.distributor!.id);
      if (linked) list.push(linked);
    }
    return list;
  }, [distributors, editingEntry]);

  const openCreate = () => {
    setEditingEntry(null);
    // Default to the month BEFORE the current month. Date handles the
    // January → previous-December rollover (and the corresponding year
    // decrement) automatically.
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    reset({
      entry_type: 'commission',
      year: prev.getFullYear(),
      month: String(prev.getMonth() + 1) as any,
      amount: '',
    });
    setShowModal(true);
  };

  const openEdit = (entry: CommissionEntry) => {
    setEditingEntry(entry);
    reset({
      entry_type: entry.entry_type,
      // Cast to any: select values are strings in the DOM; zod coerces back to numbers on submit
      manufacturer_id: String(entry.manufacturer.id) as any,
      distributor_id: entry.distributor?.id != null ? (String(entry.distributor.id) as any) : undefined,
      year: entry.year,
      month: entry.month != null ? (String(entry.month) as any) : undefined,
      amount: sanitizeAmount(entry.amount),
      region: entry.region ?? '',
      zip_code: entry.zip_code ?? '',
      notes: entry.notes ?? '',
    });
    setShowModal(true);
  };

  const onSubmit = async (data: EntryFormData) => {
    setIsSaving(true);
    try {
      const payload = {
        ...data,
        distributor_id: data.entry_type === 'commission' ? data.distributor_id : null,
      };
      if (editingEntry) {
        await commissionEntryService.update(editingEntry.id, payload);
        toast.success('Entry updated');
      } else {
        await commissionEntryService.create(payload as any);
        toast.success('Entry created');
      }
      setShowModal(false);
      loadEntries();
      loadTotals();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingEntry) return;
    if (!window.confirm(`Delete this ${TYPE_LABELS[editingEntry.entry_type]} entry?`)) return;
    try {
      await commissionEntryService.destroy(editingEntry.id);
      toast.success('Entry deleted');
      setShowModal(false);
      loadEntries();
      loadTotals();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const columns = useMemo<ColumnDef<CommissionEntry, any>[]>(
    () => [
      {
        id: 'entry_type',
        header: 'Type',
        accessorKey: 'entry_type',
        size: 130,
        // Type pill labels need their full width or "Commission" gets clipped.
        // noScale opts this column out of fitToWidth's percentage scaling.
        meta: { noScale: true },
        enableResizing: false,
        cell: ({ row }) => {
          const t = row.original.entry_type;
          return (
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                t === 'commission'
                  ? 'bg-blue-100 text-blue-800'
                  : t === 'deduction'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-green-100 text-green-800'
              }`}
            >
              {TYPE_LABELS[t]}
            </span>
          );
        },
      },
      {
        id: 'manufacturer_name',
        header: 'Manufacturer',
        accessorFn: (row) => row.manufacturer.name,
        size: 220,
      },
      {
        id: 'distributor_name',
        header: 'Distributor',
        accessorFn: (row) => row.distributor?.name ?? '—',
        size: 220,
      },
      {
        id: 'year',
        header: 'Year',
        accessorKey: 'year',
        size: 90,
      },
      {
        id: 'month',
        header: 'Month',
        accessorKey: 'month',
        size: 130,
        cell: ({ row }) => (row.original.month ? MONTHS[row.original.month - 1].label : '—'),
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorKey: 'amount',
        size: 140,
        cell: ({ row }) => (
          <span className="font-medium">
            ${parseFloat(row.original.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        id: 'region',
        header: 'Region',
        accessorKey: 'region',
        size: 130,
        cell: (info) => (info.getValue() as string | null) ?? '—',
      },
      {
        id: 'zip_code',
        header: 'Zip',
        accessorKey: 'zip_code',
        size: 110,
        cell: (info) => (info.getValue() as string | null) ?? '—',
      },
      {
        id: 'notes',
        header: 'Notes',
        accessorKey: 'notes',
        size: 240,
        cell: ({ row }) => {
          const notes = row.original.notes;
          if (!notes) return '—';
          return (
            <span className="block overflow-hidden text-ellipsis whitespace-nowrap" title={notes}>
              {notes}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        enableResizing: false,
        size: 80,
        cell: ({ row }) => (
          <div className="text-right">
            <button
              onClick={() => openEdit(row.original)}
              className="text-blue-600 hover:text-blue-900"
              title="Edit"
            >
              <Pencil className="h-4 w-4 inline" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manual Commission Reporting</h1>
          <p className="text-sm text-gray-600 mt-1">Manage commission, deduction, and bonus entries</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Add Entry
        </button>
      </div>

      {/* Totals — mirrors the KPI strip on Metabase dashboard 298 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <TotalTile label="Sum of Commissions" value={formatCurrency(totals?.sum_commissions)} />
        <TotalTile label="Sum of Deductions" value={formatCurrency(totals?.sum_deductions)} />
        <TotalTile label="Sum of Bonus Check" value={formatCurrency(totals?.sum_bonus)} />
        <TotalTile label="Sum of Net Commissions" value={formatCurrency(totals?.net_commissions)} />
        <TotalTile label="Commission Entries Count" value={formatCount(totals?.count_commissions)} />
        <TotalTile label="Deduction Entries Count" value={formatCount(totals?.count_deductions)} />
        <TotalTile label="Bonus Entries Count" value={formatCount(totals?.count_bonus)} />
        <TotalTile label="Total Entries" value={formatCount(totals?.count_total)} />
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="label">Type</label>
            <MultiSelect
              value={filters.entry_type_in}
              onChange={(v) => updateFilter('entry_type_in', v)}
              options={ENTRY_TYPE_OPTIONS}
            />
          </div>
          <div>
            <label className="label">Manufacturer</label>
            <MultiTypeahead
              value={filters.manufacturer_id_in}
              onChange={(v) => updateFilter('manufacturer_id_in', v)}
              options={manufacturers.map<MultiTypeaheadOption>((m) => ({ value: String(m.id), label: m.name }))}
              placeholder="Search manufacturers…"
            />
          </div>
          <div>
            <label className="label">Distributor</label>
            <MultiTypeahead
              value={filters.distributor_id_in}
              onChange={(v) => updateFilter('distributor_id_in', v)}
              options={distributors.map<MultiTypeaheadOption>((d) => ({ value: String(d.id), label: d.name }))}
              placeholder="Search distributors…"
            />
          </div>
          <div>
            <label className="label">Year</label>
            <MultiSelect
              value={filters.year_in}
              onChange={(v) => updateFilter('year_in', v)}
              options={yearOptions.map<MultiSelectOption>((y) => ({ value: String(y), label: String(y) }))}
            />
          </div>
          <div>
            <label className="label">Month</label>
            <MultiSelect
              value={filters.month_in}
              onChange={(v) => updateFilter('month_in', v)}
              options={MONTHS.map<MultiSelectOption>((m) => ({ value: String(m.value), label: m.label }))}
            />
          </div>
          <div>
            <label className="label">Region</label>
            <MultiSelect
              value={filters.region_in}
              onChange={(v) => updateFilter('region_in', v)}
              options={regionOptions.map<MultiSelectOption>((r) => ({ value: r, label: r }))}
            />
          </div>
          <div>
            <label className="label">Zip</label>
            <MultiSelect
              value={filters.zip_code_in}
              onChange={(v) => updateFilter('zip_code_in', v)}
              options={[
                { value: BLANK_SENTINEL, label: '(Blank)' },
                ...zipOptions.map<MultiSelectOption>((z) => ({ value: z, label: z })),
              ]}
            />
          </div>
          <div className="md:col-span-3">
            <label className="label">Search notes</label>
            <input
              className="input"
              placeholder="Search inside notes…"
              value={filters.notes_cont}
              onChange={(e) => updateFilter('notes_cont', e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <button onClick={clearFilters} className="btn btn-secondary w-full">Clear</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={entries}
        meta={meta}
        isLoading={isLoading}
        page={page}
        onPageChange={setPage}
        sorting={sorting}
        onSortingChange={setSorting}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        emptyMessage="No entries"
        fitToWidth
      />

      {/* Page-size selector (DataTable doesn't render this; we control it here) */}
      {meta && meta.pages > 0 && (
        <div className="flex justify-end text-sm">
          <label className="flex items-center gap-2 text-gray-600">
            Rows per page:
            <select
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="input py-1 px-2 text-sm w-auto"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-(--color-bg-scrim)" onClick={() => setShowModal(false)} />
            <div className="relative inline-block bg-white rounded-lg text-left shadow-xl sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="px-6 pt-5 pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{editingEntry ? 'Edit' : 'Add'} Entry</h3>
                    <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500"><X className="h-6 w-6" /></button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="label">Type</label>
                      <div className="grid grid-cols-3 gap-2">
                        {(['commission', 'deduction', 'bonus'] as CommissionEntryType[]).map((t) => (
                          <button
                            key={t}
                            type="button"
                            disabled={!!editingEntry}
                            onClick={() => setValue('entry_type', t, { shouldValidate: true })}
                            className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                              selectedType === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            } disabled:opacity-60 disabled:cursor-not-allowed`}
                          >
                            {TYPE_LABELS[t]}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="label">Manufacturer</label>
                      <Controller
                        name="manufacturer_id"
                        control={control}
                        render={({ field }) => (
                          <Typeahead
                            items={activeManufacturers}
                            value={field.value ? Number(field.value) : null}
                            onChange={(id) => field.onChange(id != null ? String(id) : '')}
                            placeholder="Search manufacturers…"
                          />
                        )}
                      />
                      {errors.manufacturer_id && <p className="mt-1 text-sm text-red-600">{errors.manufacturer_id.message}</p>}
                    </div>

                    {selectedType === 'commission' && (
                      <div>
                        <label className="label">Distributor</label>
                        <Controller
                          name="distributor_id"
                          control={control}
                          render={({ field }) => (
                            <Typeahead
                              items={activeDistributors}
                              value={field.value ? Number(field.value) : null}
                              onChange={(id) => {
                                field.onChange(id != null ? String(id) : '');
                                const dist = activeDistributors.find((d) => d.id === id);
                                setValue('region', dist?.region ?? '', { shouldDirty: true });
                                setValue('zip_code', dist?.zip_code ?? '', { shouldDirty: true });
                              }}
                              placeholder="Search distributors…"
                            />
                          )}
                        />
                        {errors.distributor_id && <p className="mt-1 text-sm text-red-600">{errors.distributor_id.message}</p>}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Year</label>
                        <input type="number" {...register('year')} className="input" />
                        {errors.year && <p className="mt-1 text-sm text-red-600">{errors.year.message}</p>}
                      </div>
                      <div>
                        <label className="label">Month</label>
                        <select {...register('month')} className="input">
                          <option value="">— None —</option>
                          {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="label">Amount</label>
                      <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                          <div className="flex items-stretch rounded-md border border-gray-300 shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 overflow-hidden">
                            <span className="flex items-center px-3 bg-gray-50 text-gray-500 border-r border-gray-300">$</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              placeholder="0"
                              value={formatAmount(field.value)}
                              onChange={(e) => field.onChange(sanitizeAmount(e.target.value))}
                              onBlur={field.onBlur}
                              ref={field.ref}
                              className="flex-1 px-3 py-2 outline-none text-right tabular-nums"
                            />
                          </div>
                        )}
                      />
                      {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
                    </div>

                    {selectedType === 'commission' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="label">Region</label>
                          <input {...register('region')} className="input" placeholder="Auto-filled from distributor" />
                        </div>
                        <div>
                          <label className="label">Zip</label>
                          <input {...register('zip_code')} className="input" placeholder="Auto-filled" />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="label">Notes</label>
                      <textarea {...register('notes')} rows={2} className="input" />
                    </div>

                    {editingEntry && <AuditMetadata record={editingEntry} />}
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-3 flex items-center gap-3 rounded-b-lg">
                  {editingEntry && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="text-red-600 hover:text-red-700 text-sm font-semibold inline-flex items-center gap-1"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  )}
                  <div className="ml-auto flex gap-3">
                    <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                    <button type="submit" disabled={isSaving} className="btn btn-primary">
                      {isSaving ? 'Saving…' : editingEntry ? 'Save Changes' : 'Create'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualCommissionReporting;
